import React, { useState } from 'react'
import {
  areCoordinatesEqual,
  geometryToLongitudeLatitude,
  getFirstRequisition,
  isSamplePartiallyEditable,
  longitudeLatitudeToGeometry,
  ModalDialog,
  Mtds,
  parsePayloadSchema,
  Sample,
  SampleFormData,
  SaveError,
  Task,
  UpdateSample,
  useGoBack,
  useUpdateSampleMutation
} from 'prove-common'
import SampleRegistrationForm from '../sample-registration-form/SampleRegistrationForm'
import {
  createSamplePayload,
  createSubSamples,
  getExternalReference,
  getExternalReferenceType,
  getPayloadWithSubSamples,
  isNameRedacted
} from '../../utils/sampleUtils'
import { IChangeEvent } from 'prove-common/rjsf'

interface SampleEditFormProps {
  sample: Sample
  newTask: Task | undefined
  showReceiptInfoOnSave?: boolean
  localLonLat?: [number, number]
  onSuccess: (oldSample: Sample) => void
}

const hasScan = (payload?: string | null) => {
  if (!payload) return false
  try {
    return JSON.parse(payload)?.skrottlappScan != null
  } catch {
    return false
  }
}

export const SampleEditForm: React.FC<SampleEditFormProps> = ({
  sample,
  newTask,
  showReceiptInfoOnSave = false,
  localLonLat,
  onSuccess
}) => {
  const [showReceiptInfoDialog, setShowReceiptInfoDialog] = useState(false)
  const [pendingUpdate, setPendingUpdate] = useState<UpdateSample | null>(null)
  const [saveError, setSaveError] = useState<unknown>(null)
  // A background refresh must not rebase the draft onto a newer server version.
  const [initialEtag] = useState(sample.etag)

  const isPartiallyEditable = isSamplePartiallyEditable(sample)
  const goBack = useGoBack(`/samples/${sample.id}`)
  const updateSample = useUpdateSampleMutation()
  const requisition = getFirstRequisition(sample)
  const payloadWithSubSamples = getPayloadWithSubSamples(
    sample,
    requisition?.payload
  )

  const sampleCoords = geometryToLongitudeLatitude(sample.geometry)
  const localCoords =
    localLonLat != null
      ? { longitude: localLonLat[0], latitude: localLonLat[1] }
      : undefined
  const hasCoordinatesChangedFromSample =
    localCoords != null &&
    sampleCoords != null &&
    !areCoordinatesEqual(sampleCoords, localCoords)

  const initialPayloadWithSubSamples = {
    ...payloadWithSubSamples,
    payload: hasCoordinatesChangedFromSample
      ? (() => {
          if (!payloadWithSubSamples?.payload) {
            return payloadWithSubSamples?.payload
          }
          const updated = { ...payloadWithSubSamples.payload }
          delete updated.vassdrag
          return updated
        })()
      : payloadWithSubSamples?.payload
  }

  let effectiveGeometry = sample.geometry
  let accuratePosition = sample.accuratePosition
  if (localLonLat != null) {
    effectiveGeometry = longitudeLatitudeToGeometry({
      longitude: localLonLat[0],
      latitude: localLonLat[1]
    })
    accuratePosition = true
  }

  const currentPosition =
    localLonLat != null
      ? { longitude: localLonLat[0], latitude: localLonLat[1] }
      : sample.geometry

  const sampleId = sample.id
  const taskId = newTask?.id ?? sample.taskId
  const sampntType = newTask?.sampntType ?? sample.sampntType
  const externalReferenceType = getExternalReferenceType(sampntType?.code)
  const sampntTypeId = sampntType?.id
  const sampleTypeId =
    newTask?.analysisType?.sampleType?.id ?? sample.sampleType?.id
  const schema =
    newTask?.analysisType?.payloadSchema?.schema ??
    requisition?.analysisType?.payloadSchema?.schema

  const saveSample = async (updatedSample: UpdateSample): Promise<void> => {
    const oldSample = sample
    setSaveError(null)
    try {
      await updateSample.mutateAsync({
        sampleId,
        body: updatedSample,
        etag: initialEtag
      })
    } catch (error) {
      setSaveError(error)
      return
    }
    setShowReceiptInfoDialog(false)
    setPendingUpdate(null)
    onSuccess(oldSample)
  }

  const onSubmit = ({ formData }: IChangeEvent<SampleFormData>): void => {
    if (formData == null) {
      Mtds.toast.danger('Kunne ikke oppdatere prøve')
      return
    }
    const externalReference = getExternalReference(
      formData,
      externalReferenceType
    )
    const orgnr = externalReference?.organisasjonsnummer
    const parsedOrgnr =
      orgnr != null && orgnr.trim() !== '' && !Number.isNaN(Number(orgnr))
        ? Number(orgnr)
        : undefined
    const payload = formData.payload
    const createSubSamplesList = createSubSamples(payload?.subSamples ?? [])
    const samplePayload = createSamplePayload(
      payload ?? {},
      externalReferenceType === 'PRODUSENT'
        ? externalReference.externalReferenceId
        : undefined
    )
    const hasProducerInfoChanged =
      externalReference?.tilsynsobjektId !== sample.oversightObject ||
      externalReference?.externalReferenceId !== sample.externalReferenceId ||
      parsedOrgnr !== sample.orgnr

    if (isPartiallyEditable) {
      if (!hasProducerInfoChanged) {
        Mtds.toast.info('Ingen endringer på produsent eller lokalitet')
        return
      }

      const hasSkrottlappScan =
        sample.subSamples?.some((s) => hasScan(s.payload)) ||
        sample.requisitions?.some((r) => hasScan(r.payload)) ||
        false
      if (hasSkrottlappScan) {
        Mtds.toast.danger(
          'Endring av produsent eller lokalitet er ikke tillatt for prøver hvor det er brukt skrottlapp-skanning'
        )
        return
      }
      const nameRedacted = isNameRedacted(formData.nameRedacted)
      if (nameRedacted == null) {
        Mtds.toast.danger(
          'Svar på om virksomhetsnavnet kun inneholder personnavn'
        )
        return
      }

      const updatedSample: UpdateSample = {
        ...sample,
        taskId,
        sampntTypeId,
        sampleTypeId,
        takenDate: new Date(formData.takenDate).toISOString(),
        externalReferenceType,
        externalReferenceId: externalReference?.externalReferenceId,
        orgnr: parsedOrgnr,
        oversightObject: externalReference?.tilsynsobjektId,
        payload: samplePayload,
        comment: formData.comment,
        nameRedacted: nameRedacted,
        subSamples: sample.subSamples?.map((subSample, index) => ({
          ...subSample,
          payload: createSubSamplesList?.[index]?.payload ?? subSample.payload
        }))
      }
      void saveSample(updatedSample)
      return
    }

    const updatedSample: UpdateSample = {
      ...sample,
      taskId,
      sampntTypeId,
      sampleTypeId,
      takenDate: new Date(formData.takenDate).toISOString(),
      externalReferenceType: externalReferenceType,
      externalReferenceId: externalReference?.externalReferenceId,
      orgnr: parsedOrgnr,
      oversightObject: externalReference?.tilsynsobjektId,
      payload: samplePayload,
      comment: formData.comment,
      geometry: effectiveGeometry,
      accuratePosition: accuratePosition,
      subSamples: createSubSamplesList
    }

    if (hasProducerInfoChanged || showReceiptInfoOnSave) {
      setSaveError(null)
      setPendingUpdate(updatedSample)
      setShowReceiptInfoDialog(true)
      return
    }

    void saveSample(updatedSample)
  }

  return (
    <>
      {!showReceiptInfoDialog && (
        <SaveError error={saveError} action='lagre prøven' />
      )}
      <SampleRegistrationForm
        schema={parsePayloadSchema(schema)}
        externalReferenceType={externalReferenceType}
        existingFormData={initialPayloadWithSubSamples}
        onSubmit={onSubmit}
        onCancel={goBack}
        isSaving={updateSample.isPending}
        isEditing={true}
        isPartiallyEditable={isPartiallyEditable}
        position={currentPosition}
      />

      <ModalDialog
        id='sample-edit-receipt-info-dialog'
        open={showReceiptInfoDialog}
        onClose={() => {
          setShowReceiptInfoDialog(false)
          setPendingUpdate(null)
        }}
      >
        <Mtds.Button
          command='close'
          commandfor='sample-edit-receipt-info-dialog'
          aria-label='Lukk'
        />
        <Mtds.Prose>
          <p>
            Når du gjør endringer i prøven, sendes det automatisk en oppdatert
            kvittering til produsenten for prøveuttaket.
          </p>
          <SaveError error={saveError} action='lagre prøven' />
          <Mtds.Flex className='mt-8 justify-center'>
            <Mtds.Button
              data-variant='primary'
              className='min-w-32 px-8 py-3 text-base'
              disabled={updateSample.isPending}
              onClick={() => {
                if (updateSample.isPending || pendingUpdate == null) {
                  return
                }
                void saveSample(pendingUpdate)
              }}
            >
              Ok
            </Mtds.Button>
          </Mtds.Flex>
        </Mtds.Prose>
      </ModalDialog>
    </>
  )
}
